import { ui } from "@/components/ui";
import ObservationEntryCardButtons from "@/features/observations/components/ObservationEntryCardButtons";
import { Route as NewRoute } from "@/routes/_app/diaries/$diaryId/home/new";
import type { StatefulObservationViewT } from "@/types/database/observations";
import { useAuth } from "@/utils/auth";
import { AlarmClockCheck, BellRing, CheckCircle2 } from "lucide-react";
import NewStateSwitch from "./NewStateSwitch";

interface Props {
	observation: StatefulObservationViewT & { index?: number };
}

const ObservationStateIcon = ({
	observation,
}: { observation: StatefulObservationViewT }) => {
	const isDue = observation.is_due;
	const isComplete = observation.observation_complete;

	return (
		<div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800 px-3 py-1.5 rounded-full">
			{isComplete ? (
				<>
					<CheckCircle2 className="w-4 h-4 text-green-500" />
					<span className="font-medium text-green-700 dark:text-green-400 text-sm">
						Complete
					</span>
				</>
			) : isDue ? (
				<>
					<BellRing className="w-4 h-4 text-destructive animate-big-wiggle" />
					<span className="font-medium text-destructive text-sm">Due</span>
				</>
			) : (
				<>
					<AlarmClockCheck className="w-4 h-4 text-blue-500" />
					<span className="font-medium text-blue-700 dark:text-blue-400 text-sm">
						Up to date
					</span>
				</>
			)}
		</div>
	);
};

const NewObservationEntryCard = ({ observation }: Props) => {
	const auth = useAuth();
	const { diaryId } = NewRoute.useParams();

	// Extract values with defaults to handle null/undefined cases
	const isNextEntryLocked = observation.next_entry?.is_locked ?? false;
	const nextEntryDueAt = observation.next_entry?.due_at ?? null;
	const nextEntryLabel = observation.next_entry?.label ?? "";
	const dueEntriesCount = observation.due_entries?.length ?? 0;

	// Get entries and due entries
	const entries = observation.entries_view ?? [];
	const dueEntries = observation.due_entries ?? [];

	return (
		<ui.Card className="flex flex-col justify-center items-center gap-y-4">
			<ui.CardHeader className="flex flex-row justify-between items-start mx-auto p-6 pb-0 w-full">
				<ui.CardTitle className="flex flex-col gap-1 w-full">
					{observation.index && (
						<span className="font-medium text-slate-600 text-sm">
							Form #{observation.index}
						</span>
					)}
					{observation.form.form_name}
				</ui.CardTitle>
				<ObservationStateIcon observation={observation} />
			</ui.CardHeader>
			<ui.CardContent className="flex flex-col items-start md:items-start px-6 pt-0 w-full">
				<NewStateSwitch
					endDate={observation.end_date}
					isComplete={observation.observation_complete}
					isNextEntryLocked={isNextEntryLocked}
					nextEntryDueAt={nextEntryDueAt}
					dueEntriesCount={dueEntriesCount}
					nextEntryLabel={nextEntryLabel}
					canMarkComplete={false}
					entries={entries}
					dueEntries={dueEntries}
				/>
			</ui.CardContent>
			<ui.CardFooter className="flex flex-col gap-2 w-full">
				<ObservationEntryCardButtons
					diaryId={diaryId}
					observationId={observation.observation_id}
					observationName={observation.observation_name ?? ""}
					isNextEntryLocked={isNextEntryLocked}
					showNextEntryButton={!isNextEntryLocked && !!nextEntryDueAt}
					showEditButton={auth.isSuperUser}
					nextEntryLabel={nextEntryLabel}
				/>
			</ui.CardFooter>
		</ui.Card>
	);
};

export default NewObservationEntryCard;
